import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Modal, Tree, Spin, Alert, Button, Space, Typography, message, Tag, Input, Select, Card, Radio, Tooltip,
} from 'antd';
import { ReloadOutlined, SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';

import {
  loadSchemaUniverse, denialsToKeySet, keysToDenials,
  categorize, schemaKey, viewKey, LAYER_KEYS,
} from './dataAccessTreeUtils';
import { getUserPermissions, setUserPermissions } from '../services/dataPermissionsService';
import { listGroups, getGroup } from '../services/dataAccessGroupsService';

const { Text } = Typography;

/**
 * Admin-only modal for managing a user's data access.
 *
 * Three modes (radio at top):
 *   - 'group'                — user inherits the group's denials only.
 *   - 'group_with_overrides' — group denials ∪ per-user overrides.
 *   - 'custom'               — no group; per-user denials only.
 *
 * Tree semantics: a CHECKED node means DENIED. Inherited group denials
 * appear as disabled-checked rows labelled "(from group)" in
 * 'group_with_overrides' mode.
 */
export default function DataPermissionsModal({ open, user, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isAdminUser, setIsAdminUser] = useState(false);

  const [schemas, setSchemas] = useState([]);
  const [viewsBySchemaUpper, setViewsBySchemaUpper] = useState({});
  const [allKeys, setAllKeys] = useState([]);

  const [mode, setMode] = useState('group');
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState(null);
  const [groupDenialKeys, setGroupDenialKeys] = useState(new Set());

  const [overrideKeys, setOverrideKeys] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  const loadGroupDenials = useCallback(async (gid, viewsMap) => {
    if (!gid) { setGroupDenialKeys(new Set()); return; }
    try {
      const detail = await getGroup(gid);
      setGroupDenialKeys(denialsToKeySet(detail.denials || [], viewsMap));
    } catch (e) {
      console.warn('Failed to load group denials:', e);
      setGroupDenialKeys(new Set());
    }
  }, []);

  const reload = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [universe, perms, groupsList] = await Promise.all([
        loadSchemaUniverse(),
        getUserPermissions(user.id),
        listGroups().catch(() => []),
      ]);
      setSchemas(universe.schemas);
      setViewsBySchemaUpper(universe.viewsBySchemaUpper);
      setAllKeys(universe.allKeys);
      setGroups(Array.isArray(groupsList) ? groupsList : []);
      setIsAdminUser(!!perms.is_admin);

      const initialMode = (perms.mode || (perms.group ? 'group' : 'custom')).toLowerCase();
      setMode(initialMode);
      setGroupId(perms.group?.id || null);

      if (perms.group?.id) {
        setGroupDenialKeys(denialsToKeySet(perms.group_denials || [], universe.viewsBySchemaUpper));
      } else {
        setGroupDenialKeys(new Set());
      }

      const ovDenials = perms.override_denials || (initialMode === 'custom' ? perms.denials : []);
      setOverrideKeys(Array.from(denialsToKeySet(ovDenials, universe.viewsBySchemaUpper)));
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { if (open) reload(); }, [open, reload]);

  // When the admin changes the group dropdown, refresh that group's denials.
  // Skip this on the very first load (reload() already set them).
  useEffect(() => {
    if (!open || loading) return;
    if (mode === 'group' || mode === 'group_with_overrides') {
      loadGroupDenials(groupId, viewsBySchemaUpper);
    } else {
      setGroupDenialKeys(new Set());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, mode]);

  const buildTreeData = useCallback((category) => {
    const q = (searchValue || '').trim().toLowerCase();
    return schemas
      .filter((s) => categorize(s.schema) === category)
      .map((s) => {
        const sUpper = s.schema.toUpperCase();
        const viewNames = viewsBySchemaUpper[sUpper] || [];
        const children = viewNames
          .filter((v) => !q || v.toLowerCase().includes(q) || s.schema.toLowerCase().includes(q))
          .map((v) => {
            const k = viewKey(s.schema, v);
            const fromGroup = groupDenialKeys.has(k);
            return {
              key: k,
              title: fromGroup ? (
                <span>{v} <Text type="secondary" style={{ fontSize: 11 }}>(from group)</Text></span>
              ) : v,
              disableCheckbox: fromGroup && mode === 'group_with_overrides',
            };
          });
        const schemaMatches = !q || s.schema.toLowerCase().includes(q) || children.length > 0;
        if (!schemaMatches) return null;
        const sKey = schemaKey(s.schema);
        const schemaFromGroup = groupDenialKeys.has(sKey);
        return {
          key: sKey,
          title: (
            <span>
              <strong>{s.schema}</strong>
              <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                ({viewNames.length} {viewNames.length === 1 ? 'view' : 'views'})
              </Text>
              {schemaFromGroup && (
                <Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>· (from group)</Text>
              )}
            </span>
          ),
          disableCheckbox: schemaFromGroup && mode === 'group_with_overrides',
          children,
        };
      })
      .filter(Boolean);
  }, [schemas, viewsBySchemaUpper, searchValue, groupDenialKeys, mode]);

  // Tree's checkedKeys are ALLOWED keys. We derive them from the denial sets
  // that we keep in state: group denials are inherited, override denials are
  // the per-user additions.
  const checkedForTree = useMemo(() => {
    if (!allKeys.length) return [];
    if (mode === 'group') {
      return allKeys.filter((k) => !groupDenialKeys.has(k));
    }
    if (mode === 'group_with_overrides') {
      const ovSet = new Set(overrideKeys);
      return allKeys.filter((k) => !groupDenialKeys.has(k) && !ovSet.has(k));
    }
    // custom
    const ovSet = new Set(overrideKeys);
    return allKeys.filter((k) => !ovSet.has(k));
  }, [mode, groupDenialKeys, overrideKeys, allKeys]);

  const handleCheck = (keys, layer) => {
    if (mode === 'group') return; // read-only
    const checkedSet = new Set(Array.isArray(keys) ? keys : (keys?.checked || []));
    // Each layer renders its own Tree and only reports its own keys, so we
    // must merge with the denials we already have for the OTHER layers —
    // otherwise checking RAW would silently re-deny everything in REFINED.
    const layerKeys = layer ? new Set(keysForCategory(layer)) : new Set(allKeys);
    // For THIS layer, denied = layer keys that are not checked.
    const layerDenied = [];
    for (const k of layerKeys) if (!checkedSet.has(k)) layerDenied.push(k);
    if (mode === 'custom') {
      setOverrideKeys((prev) => {
        const kept = prev.filter((k) => !layerKeys.has(k));
        return [...kept, ...layerDenied];
      });
      return;
    }
    // group_with_overrides: overrides are extra denials on top of the group.
    setOverrideKeys((prev) => {
      const kept = prev.filter((k) => !layerKeys.has(k));
      const extra = layerDenied.filter((k) => !groupDenialKeys.has(k));
      return [...kept, ...extra];
    });
  };

  const keysForCategory = useCallback((category) => {
    const keys = [];
    for (const s of schemas) {
      if (categorize(s.schema) !== category) continue;
      keys.push(schemaKey(s.schema));
      for (const v of (viewsBySchemaUpper[s.schema.toUpperCase()] || [])) {
        keys.push(viewKey(s.schema, v));
      }
    }
    return keys;
  }, [schemas, viewsBySchemaUpper]);

  const handleDenyAllLayer = (category) => {
    if (mode === 'group') return;
    const layerKeys = new Set(keysForCategory(category));
    setOverrideKeys((prev) => {
      const s = new Set(prev);
      for (const k of layerKeys) {
        if (mode === 'group_with_overrides' && groupDenialKeys.has(k)) continue;
        s.add(k);
      }
      return Array.from(s);
    });
  };

  const handleAllowAllLayer = (category) => {
    if (mode === 'group') return;
    const layerKeys = new Set(keysForCategory(category));
    setOverrideKeys((prev) => prev.filter((k) => !layerKeys.has(k)));
  };

  const handleSave = async () => {
    if (!user?.id) return;
    if ((mode === 'group' || mode === 'group_with_overrides') && !groupId) {
      message.error('Pick a group first or switch to Custom.');
      return;
    }
    setSaving(true);
    try {
      const overrideDenials = mode === 'group'
        ? []
        : keysToDenials(overrideKeys, schemas, viewsBySchemaUpper);
      await setUserPermissions(user.id, {
        mode,
        groupId: (mode === 'group' || mode === 'group_with_overrides') ? groupId : null,
        denials: overrideDenials,
      });
      message.success(`Saved data access for ${user.email}`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      message.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const summary = useMemo(() => {
    const total = new Set([
      ...Array.from(groupDenialKeys),
      ...(mode === 'group' ? [] : overrideKeys),
    ]);
    let groupSchemaCount = 0;
    for (const k of groupDenialKeys) if (k.startsWith('S:')) groupSchemaCount += 1;
    let totalSchemas = 0;
    for (const k of total) if (k.startsWith('S:')) totalSchemas += 1;
    return {
      total_keys: total.size,
      total_schemas: totalSchemas,
      group_schemas: groupSchemaCount,
      override_count: mode === 'group' ? 0 : overrideKeys.length,
    };
  }, [groupDenialKeys, overrideKeys, mode]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={user ? `Data Access — ${user.email}` : 'Data Access'}
      width="92vw"
      style={{ top: 12, maxWidth: 1600 }}
      bodyStyle={{ paddingTop: 12, maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}
      footer={[
        <Button key="reload" icon={<ReloadOutlined />} onClick={reload} disabled={loading || saving} style={{ float: 'left' }}>
          Refresh
        </Button>,
        <Button key="cancel" onClick={onClose} disabled={saving}>Cancel</Button>,
        <Button key="save" type="primary" loading={saving} disabled={loading || isAdminUser} onClick={handleSave}>
          Save
        </Button>,
      ]}
    >
      {isAdminUser && (
        <Alert
          type="info"
          showIcon
          message="This user is an Administrator and always has full data access. Changes here have no effect."
          style={{ marginBottom: 12 }}
        />
      )}
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

      <Spin spinning={loading}>
        <Card
          size="small"
          style={{
            marginBottom: 12,
            background: 'linear-gradient(135deg, #f0f5ff 0%, #f6f8fa 100%)',
            border: '1px solid #d6e4ff',
            borderRadius: 8,
          }}
          bodyStyle={{ padding: '12px 16px' }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
            {/* Mode picker */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              <Text strong style={{ fontSize: 12, color: '#1677ff', letterSpacing: 0.4 }}>
                ACCESS MODE
                <Tooltip
                  title={(
                    <span>
                      <b>Use a group</b>: user inherits the group's denials only.<br />
                      <b>Group + overrides</b>: group denials plus extra per-user denials you check below.<br />
                      <b>Custom</b>: no group — denials below are this user's only restrictions.
                    </span>
                  )}
                >
                  <InfoCircleOutlined style={{ marginLeft: 6, color: '#1677ff' }} />
                </Tooltip>
              </Text>
              <Radio.Group
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                disabled={isAdminUser}
                optionType="button"
                buttonStyle="solid"
                size="middle"
              >
                <Radio.Button value="group">Use a group</Radio.Button>
                <Radio.Button value="group_with_overrides">Group + overrides</Radio.Button>
                <Radio.Button value="custom">Custom (no group)</Radio.Button>
              </Radio.Group>
            </div>

            {/* Group selector */}
            {(mode === 'group' || mode === 'group_with_overrides') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 360px', minWidth: 0 }}>
                <Text strong style={{ fontSize: 12, color: '#1677ff', letterSpacing: 0.4 }}>
                  DATA ACCESS GROUP
                </Text>
                <Select
                  showSearch
                  size="middle"
                  placeholder="Pick a group…"
                  style={{ width: '100%', maxWidth: 480 }}
                  value={groupId}
                  onChange={setGroupId}
                  disabled={isAdminUser}
                  optionFilterProp="label"
                  options={groups.map((g) => ({
                    value: g.id,
                    label: g.name,
                    denial_schema_count: g.denial_schema_count,
                    denial_table_count: g.denial_table_count,
                  }))}
                  optionRender={(opt) => (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontWeight: 500 }}>{opt.data.label}</span>
                      <Space size={4}>
                        <Tag color="geekblue" style={{ margin: 0, fontSize: 11 }}>
                          {opt.data.denial_schema_count} sch
                        </Tag>
                        <Tag color="purple" style={{ margin: 0, fontSize: 11 }}>
                          {opt.data.denial_table_count} views
                        </Tag>
                      </Space>
                    </div>
                  )}
                />
              </div>
            )}
          </div>
        </Card>

        <Card size="small" style={{ marginBottom: 12, background: '#f6f8fa' }} bodyStyle={{ padding: '8px 12px' }}>
          <Space size="middle" wrap>
            {mode !== 'custom' && (
              <Text>From group: <b>{summary.group_schemas}</b> schema-level + (its views).</Text>
            )}
            {mode !== 'group' && (
              <Text>Overrides: <b>{summary.override_count}</b> additional keys.</Text>
            )}
            <Text type="secondary">
              Total effective denials: <b>{summary.total_schemas}</b> schemas + {Math.max(summary.total_keys - summary.total_schemas, 0)} views (incl. inherited).
            </Text>
          </Space>
        </Card>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <Input
            placeholder="Search schema or view…"
            prefix={<SearchOutlined />}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            allowClear
            style={{ maxWidth: 320 }}
          />
          <Space>
            <Tag color="green">Checked = allowed</Tag>
            {mode === 'group_with_overrides' && (
              <Tag color="default">Group-denied rows are locked</Tag>
            )}
          </Space>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {LAYER_KEYS.map((label) => {
            const treeData = buildTreeData(label);
            const color = label === 'RAW' ? 'magenta' : label === 'REFINED' ? 'geekblue' : 'green';
            const disabledTree = isAdminUser || mode === 'group';
            return (
              <Card
                key={label}
                size="small"
                title={
                  <Space>
                    <Tag color={color} style={{ marginRight: 0 }}>{label}</Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {treeData.length} {treeData.length === 1 ? 'schema' : 'schemas'}
                    </Text>
                  </Space>
                }
                extra={
                  <Space size={4}>
                    <Button size="small" type="link"
                      onClick={() => handleAllowAllLayer(label)}
                      disabled={disabledTree || treeData.length === 0}>
                      Allow all
                    </Button>
                    <Button size="small" type="link" danger
                      onClick={() => handleDenyAllLayer(label)}
                      disabled={disabledTree || treeData.length === 0}>
                      Deny all
                    </Button>
                  </Space>
                }
                bodyStyle={{ padding: 8, height: 'calc(92vh - 320px)', overflow: 'auto' }}
                style={{ height: '100%' }}
              >
                {treeData.length === 0 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>No {label.toLowerCase()} schemas.</Text>
                ) : (
                  <Tree
                    checkable
                    selectable={false}
                    treeData={treeData}
                    checkedKeys={checkedForTree}
                    onCheck={(keys) => handleCheck(keys, label)}
                    disabled={disabledTree}
                    defaultExpandAll={false}
                  />
                )}
              </Card>
            );
          })}
        </div>
      </Spin>
    </Modal>
  );
}
